import React from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDPrimaryActionButton,
  FDBodyText,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import MetaLayoutRemainingFields from 'Components/UI/Meta/MetaForm/Components/MetaLayout/MetaLayoutRemainingFields';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import WizardStepDeleteButton from 'Views/Page/Content/Modal/Layouts/DefaultFDWizardStep/Layout/WizardStepDeleteButton';
import { DialogActions, DialogTitle, DialogContent } from 'Views/Page/Content/Modal/Components';
import { useModalContextSubmissionSuccessful } from 'Hooks/useRefreshEvent';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useMobileLayout from 'Hooks/useMobileLayout';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { dataAttrs } from 'Helpers/Testing';

const Layout = () => {
  const { resource, onSubmit, saving } = useMetaLayoutContext();
  const { clearModalCollection, refreshModalCollection } = useModalContext();
  const { displayErrorMessage } = useSnackbarContext();
  const { refreshPageContentCollection } = usePageContentContext();
  const [loading, setLoading] = React.useState(false);
  const isMobile = useMobileLayout();

  async function handleDelete() {
    await resource.delete();
    refreshPageContentCollection();
  }


  useModalContextSubmissionSuccessful((evt, clearModalCollectionDependency) => {
    switch (evt.detail.response.meta().type()) {
      case 'householdExpenseCategoryPX':
        clearModalCollectionDependency();
        break;
      /* no default */
    }
  }, [clearModalCollection]);

  const handleSaveAndAddAnother = async () => {
    try {
      setLoading(true);
      await resource.submit();
      await refreshModalCollection();
      refreshPageContentCollection(); // Not awaiting because the user should be able to interact with the new form before we finish refreshing - we can do this refresh in the background
    } catch (err) {
      displayErrorMessage(err, 'Error saving');
    } finally {
      setLoading(false);
    }
  };

  const onDeleteSuccess = async () => {
    clearModalCollection();
  };

  const isEdit = resource.type() !== 'AddExpensePXStep';

  const stackProps = isMobile ? { direction: 'column' } : { direction: 'row' };

  return (
    <>
      <DialogTitle value={isEdit ? 'Edit expense' : 'Tell us about this expense:'} />
      <DialogContent scrollKey={resource?.type() + resource?.id()}>
        <FDStack direction={'column'} alignItems={'stretch'} spacing={3}>
          <FDStack spacing={2} {...stackProps}>
            <MetaControlField attr={'name'} flex={1} helperTextValue={'E.g. "Home goods" or "Hobbies"'} />
            <MetaControlField attr={'value'} flex={1} helperTextValue={'E.g. $192'} />
          </FDStack>
          <FDStack direction="column" spacing={1}>
            <FDBodyText value="Expense details" />
            <FDStack spacing={2} {...stackProps}>
              <MetaControlField attr={'expenseCategoryName'} />
              <MetaControlField attr={'frequency'} />
            </FDStack>
            <FDStack spacing={2} {...stackProps}>
              <MetaControlField attr={'memberSaysCanBeReduced'} />
              <MetaControlField attr={'reductionPermitted'} />
            </FDStack>
          </FDStack>
          <MetaLayoutRemainingFields />
          <FDStack direction="column" spacing={2}>
            <FDStack spacing={1} direction="column">
              <MetaControlField attr={'startType'} />
              <MetaControlField attr={'startDate'} />
              <MetaControlField attr={'startAtAge'} />
            </FDStack>
            <FDStack spacing={1} direction="column">
              <MetaControlField attr={'endType'} />
              <MetaControlField attr={'endDate'} />
              <MetaControlField attr={'endAtAge'} />
            </FDStack>
          </FDStack>

          {resource.meta().canDelete() ? (
            <WizardStepDeleteButton
              onDelete={handleDelete}
              onDeleteSuccess={onDeleteSuccess}
              dialogProps={{
                title: 'Are you sure?',
                message: 'Removing this item will delete all of its information. This cannot be undone.',
                confirmLabel: 'Remove',
              }}
            />
          ) : null}
        </FDStack>
      </DialogContent>
      <DialogActions>
        {!isEdit && (
          <FDSecondaryActionButton
            {...dataAttrs('save-and-add-another-btn', resource)}
            loading={saving || loading}
            onClick={handleSaveAndAddAnother}
            label="Save and add another"
          />
        )}
        <FDPrimaryActionButton
          {...dataAttrs('save-and-finish-btn', resource)}
          loading={saving || loading}
          onClick={() => {
            onSubmit();
          }}
          label={isEdit ? 'Submit' : 'Save and finish'}
        />
      </DialogActions>
    </>
  );
};

export default Layout;
